import { FESTIVAL } from '../../data/full/festival';
import type { GameContent } from '../../data/types';
import { t } from '../../shared/i18n';
import { SUN_PALETTES } from '../../shared/palette';
import { toEnd } from '../../sim/full/festival';
import { playedTempo, setOf } from '../../sim/lineup';
import type { FestivalState, SimState } from '../../sim/state';
import { el, setText } from '../dom';
import { formatDuration, formatNumber } from '../format';
import type { HudParts, HudPart } from '../hud-parts';
import './festival.css';

// 1 to 4.
function stageNumber(festival: FestivalState): number {
  return festival.scenes.length;
}

export function stageOf(festival: FestivalState, total = FESTIVAL.setIds.length): string {
  return t('Scène {stage} sur {total}', { stage: stageNumber(festival), total });
}

export function gainLine(state: Pick<SimState, 'enemyScale'>): string {
  const scale = state.enemyScale;
  if (scale === undefined) {
    return '';
  }
  return t('{percent} % de vie et de coups en plus pour les bad vibes', {
    percent: formatNumber(Math.round((scale.hp - 1) * 100)),
  });
}

// The farthest player's way to its end, at their own pace; a player whose peer left is not waited for.
export function walkLeft(state: SimState): number {
  const side = state.festival?.taken?.side;
  if (side === undefined) {
    return 0;
  }
  return Math.max(
    0,
    ...state.players
      .filter((player) => player.gone !== true)
      .map(
        (player) =>
          Math.max(0, toEnd(player, state.arena, side) - FESTIVAL.path.endDepth) /
          Math.max(player.speed, 1),
      ),
  );
}

export function createFestivalHud({ ribbon, overlay }: HudParts): HudPart {
  const dots = el('div', 'ui-fest');
  dots.setAttribute('role', 'img');
  const points = FESTIVAL.setIds.map(() => dots.appendChild(el('span', 'ui-fest__dot')));
  const toward = el('div', 'ui-fest__toward');
  const towardName = el('span', 'ui-fest__name');
  const walk = el('span', 'ui-fest__walk');
  walk.setAttribute('role', 'timer');
  toward.append(el('span', 'ui-fest__label', t('Vers')), towardName, walk);
  ribbon.prepend(dots);
  ribbon.append(toward);

  const card = el('div', 'ui-fest__card');
  card.setAttribute('role', 'status');
  for (const token of ['sol', 'or', 'noyau', 'texte'] as const) {
    card.style.setProperty(`--${token}`, SUN_PALETTES.nuit[token]);
  }
  const kicker = el('span', 'ui-fest__kicker');
  const title = el('span', 'ui-fest__title');
  const line = el('span', 'ui-fest__line');
  card.append(kicker, title, line);
  card.hidden = true;
  overlay.append(card);

  return {
    update(state: SimState, content: GameContent) {
      const { festival } = state;
      dots.hidden = festival === undefined;
      toward.hidden = festival?.phase !== 'path';
      card.hidden = true;
      ribbon.toggleAttribute('data-path', festival?.phase === 'path');
      if (festival === undefined) {
        return;
      }
      const now = stageNumber(festival) - 1;
      const walking = festival.phase === 'path';
      points.forEach((point, index) => {
        const held = index < now || (index === now && (walking || festival.phase === 'sunrise'));
        point.dataset.state = held
          ? 'done'
          : index === now || (walking && index === now + 1)
            ? 'now'
            : 'next';
      });
      dots.setAttribute('aria-label', stageOf(festival));

      const taken = festival.taken;
      if (walking && taken !== undefined) {
        setText(towardName, t(setOf(content, taken.setId).name));
        setText(walk, t('{time} de marche', { time: formatDuration(walkLeft(state)) }));
        return;
      }
      const stage = setOf(content, state.setId);
      const scene = festival.scenes.at(-1);
      if (festival.phase === 'sunrise') {
        card.hidden = false;
        card.dataset.kind = 'sunrise';
        line.hidden = false;
        setText(kicker, `${t('Sunrise')} · ${stageOf(festival)}`);
        setText(title, t('{stage} a tenu', { stage: t(stage.name) }));
        setText(
          line,
          state.players.length > 1
            ? t(
                'Reprenez vos pièges, puis retrouvez-vous tous dans la même sortie. Elle se prend après deux mesures.',
              )
            : t(
                'Reprends tes pièges, puis entre dans une sortie. Elle se prend après deux mesures.',
              ),
        );
        return;
      }
      const since = state.tick - (scene?.fromTick ?? Number.NEGATIVE_INFINITY);
      if (since >= 0 && since < playedTempo(stage, state).ticksPerBar) {
        card.hidden = false;
        card.dataset.kind = 'arrival';
        setText(kicker, stageOf(festival));
        setText(title, t(stage.name));
        setText(line, gainLine(state));
        line.hidden = line.textContent === '';
      }
    },
  };
}
